import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { editorStore } from '../../../../store/editorStore';
import { floorplannerStore } from '../../../../store/floorplannerStore';
import { isBackdropImageType, isVoidType } from '../../../../types/floorplannerTypes';
import ColorField from '../components/ColorField';
import LineTypePicker from '../components/LineTypePicker';
import NumberField from '../components/NumberField';
import PanelSection from '../components/PanelSection';
import type { PropertyPanelController } from '../types';
import {
  getObjectLineColor,
  panelAppearanceRowClassName,
  panelRowControlClassName,
  panelRowLabelClassName,
} from '../propertyPanelUtils';

type LineAppearanceSectionProps = Pick<PropertyPanelController, 'linePropsOpen' | 'setLinePropsOpen' | 'lineTypeOptions' | 'updateGlobalState'> & {
  grouped: PropertyPanelController['grouped'];
};

const LineAppearanceSection: React.FC<LineAppearanceSectionProps> = ({
  grouped,
  linePropsOpen,
  setLinePropsOpen,
  lineTypeOptions,
  updateGlobalState,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');
  const {
    lineEditableSelections,
    firstLineEditableSelection,
    firstLiveLineEditableSelection,
    sharedLineColor,
    sharedLineWeight,
    sharedLineType,
    isAnyTextSelected,
  } = grouped;
  const selectedIdsKey = editorStore.selections.map((selection) => selection.id).join('|');
  const [lineWeightDraft, setLineWeightDraft] = useState<string | null>(null);
  const disabledIn3D = editorStore.view3D;

  useEffect(() => {
    setLineWeightDraft(null);
  }, [selectedIdsKey]);

  if (
    editorStore.selections.length === 0 ||
    lineEditableSelections.length === 0 ||
    isAnyTextSelected ||
    editorStore.selections.every((selection) => isBackdropImageType(floorplannerStore.symbolsMap.get(selection.id))) ||
    editorStore.selections.some((selection) => isVoidType(floorplannerStore.symbolsMap.get(selection.id)))
  ) {
    return null;
  }

  return (
    <PanelSection
      title={t('propertyPanel.line')}
      collapsible
      open={linePropsOpen}
      onToggle={() => setLinePropsOpen(!linePropsOpen)}
      titleClassName={disabledIn3D ? 'text-gray-400' : ''}
    >
      <div
        className={`space-y-0.5 px-3 pb-3 pt-0 ${disabledIn3D ? 'text-gray-400' : ''}`}
        aria-disabled={disabledIn3D}
      >
        {firstLineEditableSelection && (
          <div key={firstLineEditableSelection.id}>
            <ColorField
              value={sharedLineColor ?? getObjectLineColor(firstLiveLineEditableSelection ?? firstLineEditableSelection, floorplannerStore.lineColor)}
              mixed={lineEditableSelections.length > 1 && sharedLineColor === undefined}
              mixedLabel={mixed}
              onChange={(value) => {
                if (!disabledIn3D) {
                  updateGlobalState(firstLineEditableSelection, 'lineColor', value);
                }
              }}
              disabled={disabledIn3D}
            />
            <div className={panelAppearanceRowClassName}>
              <div className={panelRowLabelClassName}>
                {t('propertyPanel.lineWeight')}
              </div>
              <NumberField
                label={t('propertyPanel.lineWeight')}
                type="text"
                inputMode="decimal"
                value={lineWeightDraft ?? sharedLineWeight ?? ''}
                placeholder={lineEditableSelections.length > 1 ? mixed : undefined}
                disabled={disabledIn3D}
                fieldClassName="block w-[88px] shrink-0"
                labelClassName="sr-only"
                inputClassName={panelRowControlClassName}
                onChange={(nextValue) => {
                  if (disabledIn3D) {
                    return;
                  }

                  const value = Number(nextValue.replace(',', '.'));

                  setLineWeightDraft(nextValue);

                  if (nextValue !== '' && Number.isFinite(value) && value > 0) {
                    updateGlobalState(firstLineEditableSelection, 'lineWeight', value);
                  }
                }}
                onFocus={(event) => {
                  if (disabledIn3D) {
                    return;
                  }

                  setLineWeightDraft(sharedLineWeight === undefined ? '' : String(sharedLineWeight));
                  event.target.select();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.currentTarget.blur();
                  }
                }}
                onBlur={() => setLineWeightDraft(null)}
              />
            </div>
            <div className={panelAppearanceRowClassName}>
              <div className={panelRowLabelClassName}>{t('propertyPanel.style')}</div>
              <LineTypePicker
                value={sharedLineType}
                options={lineTypeOptions}
                mixed={lineEditableSelections.length > 1 && sharedLineType === undefined}
                mixedLabel={mixed}
                disabled={disabledIn3D}
                onChange={(nextType) =>
                  updateGlobalState(firstLineEditableSelection, 'lineType', nextType)
                }
              />
            </div>
          </div>
        )}
      </div>
    </PanelSection>
  );
};

export default LineAppearanceSection;
